import { Injectable, Injector, NgZone } from '@angular/core';

import { environment } from '../../../../../environments/environment';
import { BUILD_INFO } from '../../../../../environments/build-info';
import { OpenPanelService } from '../../openPanel.service';
import { RestaurantService } from '../../restaurant.service';
import { OrderFlowSessionService } from '../order-flow-session.service';
import { CLIENT_CODE_VERSION } from '../order-flow-trace.service';
import { lerDoNotTrack, slugDoPath } from '../gate-utils';
import {
  DecisaoCarga, GravacoesConfig, GravacoesGateContext, MotivoBloqueio,
  decidirAgendamento, decidirCarga,
} from './gravacoes-config';
import type { ContextoGravacao, GravacoesRuntime, ResultadoInicio } from './gravacoes-runtime';

/**
 * GravacoesService — orquestra o gravador de sessão (rrweb) sem nunca importá-lo.
 *
 * Mesma divisão de trabalho do OpenReplay (que este serviço SUCEDE):
 *   - `gravacoes-config`     decide SE este device grava (gates puros);
 *   - `gravacao-privacidade` decide O QUE não pode ser gravado (compartilhado);
 *   - este serviço           decide QUANDO carrega e liga uma coisa na outra;
 *   - `gravacoes-runtime`    chunk lazy, o único que fala com o `@rrweb/record`;
 *   - `gravacoes-transporte` fecha, comprime e envia os lotes (puro).
 *
 * Carrega no idle depois do `load`, e só nos devices dentro da coorte; o gate
 * de LOJA roda na carga, porque só ali a loja existe. Ver GRAVACOES.md.
 */

export type EstadoGravacoes = 'inativo' | 'bloqueado' | 'agendado' | 'carregando' | 'ativo' | 'parado' | 'falhou';

const ATRASO_POS_LOAD_MS = 3000;
const IDLE_TIMEOUT_MS = 8000;

@Injectable({ providedIn: 'root' })
export class GravacoesService {
  private estado: EstadoGravacoes = 'inativo';
  private motivo: MotivoBloqueio | string = '';
  private runtime: GravacoesRuntime | null = null;
  private agendado = false;
  private sessionId = '';

  constructor(
    private session: OrderFlowSessionService,
    private zone: NgZone,
    private injector: Injector,
  ) { }

  /** Ponto de entrada (APP_INITIALIZER). Síncrono e barato: só decide e agenda. */
  boot(): void {
    try {
      const decisao = this.decidirAgora(decidirAgendamento);
      this.motivo = decisao.motivo;
      if (!decisao.carregar) {
        this.estado = 'bloqueado';
        this.expor();
        return;
      }
      this.estado = 'agendado';
      this.agendarCarga();
      this.expor();
    } catch (e) {
      this.estado = 'falhou';
      this.expor();
    }
  }

  get estadoAtual(): EstadoGravacoes { return this.estado; }
  get motivoBloqueio(): string { return this.motivo; }
  get sessaoAtual(): string { return this.sessionId; }

  /** Atualiza o contexto DENTRO da sessão (outra loja, outro pedido). */
  atualizarContexto(parcial: Partial<ContextoGravacao>): void {
    if (this.runtime) this.runtime.setContexto(parcial);
  }

  /**
   * `protected`, e não lido inline: é o ponto de substituição do spec — sem
   * isso todo teste veria o `environment` de dev (endpoint vazio).
   */
  protected config(): GravacoesConfig | undefined {
    const base = (environment as any).gravacoes as GravacoesConfig | undefined;
    // Override por query param — SÓ no ambiente local (`permitirLocalhost`):
    // `?grcfg=clientSamplePct:0` desliga; `?grcfg=ingestPoint:http://127.0.0.1:4790/i`
    // aponta a bancada para outro coletor sem rebuild.
    if (base && base.permitirLocalhost) {
      try {
        const q = new URLSearchParams(window.location.search).get('grcfg');
        if (q) {
          const over: any = { ...base };
          for (const par of q.split(',')) {
            const i = par.indexOf(':');
            if (i < 0) continue;
            const chave = par.slice(0, i).trim();
            const val = par.slice(i + 1).trim();
            if (chave === 'clientSamplePct') over.clientSamplePct = Number(val);
            else if (chave === 'ingestPoint') over.ingestPoint = val;
          }
          (window as any).__mpGrCfg = over;
          return over as GravacoesConfig;
        }
      } catch (e) { /* query malformada não pode derrubar a decisão */ }
    }
    return base;
  }

  protected ambiente(): GravacoesGateContext {
    const win: any = typeof window !== 'undefined' ? window : {};
    const nav: any = typeof navigator !== 'undefined' ? navigator : {};
    const info = this.infoDaLoja();
    return {
      production: !!environment.production,
      hostname: (win.location && win.location.hostname) || '',
      isCypress: !!win.Cypress,
      doNotTrack: lerDoNotTrack(nav, win),
      deviceId: this.deviceIdSeguro(),
      restaurantId: info ? (info.id ?? null) : null,
      restaurantModules: info ? (info.modules ?? null) : null,
    };
  }

  private infoDaLoja(): any | null {
    try {
      const svc = this.injector.get(RestaurantService, null);
      return (svc && (svc as any).restaurant && (svc as any).restaurant.info) || null;
    } catch (e) {
      return null;
    }
  }

  private decidirAgora(
    regra: (cfg: GravacoesConfig | undefined, ctx: GravacoesGateContext) => DecisaoCarga,
  ): DecisaoCarga {
    return regra(this.config(), this.ambiente());
  }

  private deviceIdSeguro(): string {
    try { return this.session.deviceId || ''; } catch (e) { return ''; }
  }

  private agendarCarga(): void {
    this.zone.runOutsideAngular(() => {
      const depois = () => setTimeout(() => this.emIdle(() => this.carregar()), ATRASO_POS_LOAD_MS);
      try {
        if (typeof document !== 'undefined' && document.readyState === 'complete') depois();
        else if (typeof window !== 'undefined') window.addEventListener('load', depois, { once: true } as any);
        else depois();
      } catch (e) {
        depois();
      }
    });
  }

  protected emIdle(fn: () => void): void {
    const w: any = typeof window !== 'undefined' ? window : null;
    try {
      if (w && typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT_MS });
        return;
      }
    } catch (e) { /* cai no setTimeout */ }
    setTimeout(fn, 0);
  }

  /** O `import()` dinâmico — a fronteira do chunk. */
  protected carregar(): void {
    if (this.agendado) return;
    this.agendado = true;

    const decisao = this.decidirAgora(decidirCarga);
    if (!decisao.carregar) {
      this.estado = 'bloqueado';
      this.motivo = decisao.motivo;
      this.expor();
      return;
    }

    this.estado = 'carregando';
    this.expor();

    import('./gravacoes-runtime')
      .then((mod) => {
        this.runtime = mod.initGravacoesRuntime({
          cfg: this.config() as GravacoesConfig,
          contexto: this.contextoAtual(),
          aoIniciar: (r: ResultadoInicio) => {
            this.sessionId = r.sessionId;
            if (!r.ok) { this.estado = 'falhou'; this.motivo = r.motivo; }
            else this.publicarNoOpenPanel(r.sessionId);
            this.expor();
          },
          aoParar: (motivo: string) => {
            this.estado = 'parado';
            this.motivo = motivo;
            this.expor();
          },
        });
        if (this.runtime && this.estado !== 'falhou') this.estado = 'ativo';
        if (!this.runtime) this.estado = 'falhou';
        this.expor();
      })
      .catch(() => {
        this.estado = 'falhou';
        this.expor();
      });
  }

  /** Ponte para o funil: uma linha do OpenPanel vira um clique para a gravação. */
  private publicarNoOpenPanel(sid: string): void {
    if (!sid) return;
    try {
      const op = this.injector.get(OpenPanelService, null);
      if (op) op.setGlobalProperty('gravacao_sid', sid);
    } catch (e) { /* conveniência */ }
  }

  private contextoAtual(): ContextoGravacao {
    let restaurant = '';
    try { restaurant = slugDoPath(window.location.pathname); } catch (e) { /* vazio */ }
    const info = this.infoDaLoja();
    return {
      deviceId: this.deviceIdSeguro(),
      attemptId: this.seguro(() => this.session.attemptId),
      restaurant,
      restaurantId: info ? (info.id ?? null) : null,
      clientCodeVersion: CLIENT_CODE_VERSION,
      addressFlow: this.seguro(() => this.session.addressFlow),
      release: BUILD_INFO.release,
    };
  }

  private seguro(fn: () => string): string {
    try { return fn() || ''; } catch (e) { return ''; }
  }

  /** Sonda: `window.__mpGravacoes` no console responde "por que não grava?". */
  private expor(): void {
    try {
      const rt = this.runtime;
      (window as any).__mpGravacoes = {
        estado: this.estado,
        motivo: this.motivo,
        sessionId: this.sessionId,
        estatisticas: () => (rt ? rt.estatisticas() : null),
        flush: () => { if (rt) rt.flush(); },
      };
    } catch (e) { /* ignorado */ }
  }
}
